import { FC, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import axios from 'axios'
import { ChartsWidget9 } from '../../../../../_metronic/partials/widgets/charts/ChartsWidget9'
import { secondsToHoursFormatNotSeconds } from '../../../../../_metronic/helpers/AssetHelpers'
import './PublicClient.css'

type CycleData = {
  budgeted_hours: number
  used_time: number
  used_time_percentage: number
  cycle: number
  begin_date: string
  end_date: string
  cycles_filter: Array<{
    id: number
    begin_date: string
    end_date: string
    cycle: number
  }>
}

type Props = {
  clientUrl: string
  isAuthenticated?: boolean
  token?: string | null
}

const PublicCycleView: FC<Props> = ({ clientUrl, isAuthenticated = false, token = null }) => {
  const intl = useIntl()
  const [cycleData, setCycleData] = useState<CycleData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')

  useEffect(() => {
    fetchCycleData()
  }, [clientUrl])

  const fetchCycleData = async (beginDate?: string, endDate?: string) => {
    try {
      setLoading(true)
      setError(null)

      // Detecta o ambiente baseado na URL atual
      const apiUrl = process.env.REACT_APP_API_URL; 

      // Use protected endpoint if authenticated (with projectId), otherwise use public endpoint (with clientUrl)
      const baseUrl = isAuthenticated 
        ? `${apiUrl}/api/client-portal/project/${clientUrl}` // clientUrl is actually projectId when authenticated
        : `${apiUrl}/api/public/client/${clientUrl}`
      
      let url = `${baseUrl}/cycle`
      if (beginDate && endDate) {
        url += `?begin_date=${beginDate}&end_date=${endDate}`
      }

      const headers: Record<string, string> = {}
      if (isAuthenticated && token) {
        headers['Authorization'] = `Bearer ${token}`
        headers['Content-Type'] = 'application/json'
      }

      const response = await axios.get(url, { headers })
      const data = response.data?.data || response.data

      if (data) {
        setCycleData(data)
        // Inicializar com o primeiro ciclo disponível se não há período selecionado
        if (!selectedPeriod && data.cycles_filter && data.cycles_filter.length > 0) {
          const firstCycle = data.cycles_filter[0]
          setSelectedPeriod(`${firstCycle.begin_date},${firstCycle.end_date}`)
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados do ciclo:', error)
      setError(error.response?.data?.message || 'Erro ao carregar dados do ciclo')
    } finally {
      setLoading(false)
    }
  }

  const handlePeriodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    setSelectedPeriod(value)
    
    if (value) {
      const [beginDate, endDate] = value.split(',')
      fetchCycleData(beginDate, endDate)
    } else {
      // Se não há valor selecionado, buscar dados do ciclo atual
      fetchCycleData()
    }
  }

  const calculateProgress = (): number => {
    if (!cycleData || cycleData.budgeted_hours === 0) return 0
    // Usar o percentual já calculado pelo backend
    return Math.round(cycleData.used_time_percentage || 0)
  }

  if (loading) {
    return (
      <div className='pc-state'>
        <div className='spinner-border pc-spinner' role='status'>
          <span className='visually-hidden'>Carregando...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='pc-state' role='alert'>
        <i className='bi bi-exclamation-triangle' aria-hidden='true'></i>
        <p className='pc-state-title'>Não foi possível carregar os dados do ciclo</p>
        <p className='mb-0'>{error}</p>
      </div>
    )
  }

  if (!cycleData) {
    return null
  }

  const progress = calculateProgress()
  const hasBudget = !!cycleData.budgeted_hours && cycleData.budgeted_hours > 0

  return (
    <section className='pc-section'>
      <div className='pc-section-header'>
        <h2 className='pc-section-title'>{intl.formatMessage({id: 'PROJECT.LABEL.CYCLE_VISION'})}</h2>
        <div className='pc-period'>
          {cycleData.begin_date ? (
            <>
              <label htmlFor='period'>{intl.formatMessage({id: 'PROJECT.LABEL.PERIOD'})}</label>
              <select
                className='form-select form-select-sm pc-select'
                id='period'
                onChange={handlePeriodChange}
                value={selectedPeriod}
              >
                {cycleData.cycles_filter ? (
                  cycleData.cycles_filter.map((item) => (
                    <option
                      value={`${item.begin_date},${item.end_date}`}
                      key={item.id}
                    >{`${new Date(item.begin_date).toLocaleDateString(intl.locale, {
                      timeZone: 'UTC',
                    })} a ${new Date(item.end_date).toLocaleDateString(intl.locale, {
                      timeZone: 'UTC',
                    })}`}</option>
                  ))
                ) : (
                  <option value=''>{intl.formatMessage({id: 'PROJECT.LABEL.NO_CYCLE_DATA'})}</option>
                )}
              </select>
            </>
          ) : (
            <span className='pc-section-meta'>
              {intl.formatMessage({id: 'PROJECT.WARNING.SEE_PROJECT_CYCLES'})}
            </span>
          )}
        </div>
      </div>

      <div className='row align-items-center g-4'>
        <div className='col-md-6'>
          {hasBudget ? (
            <ChartsWidget9 chartColor={'brand'} percentage={progress} />
          ) : (
            <ChartsWidget9
              chartColor={'brand'}
              percentage={secondsToHoursFormatNotSeconds(cycleData.used_time)}
              chartHeight='250px'
              is_hour={true}
            />
          )}
        </div>
        <div className='col-md-6'>
          {/* Apenas as métricas de horas, sem informações financeiras */}
          <div className='pc-stat-row'>
            <span>{intl.formatMessage({id: 'PROJECT.LABEL.HOURS_CONTRACTED'})}</span>
            <strong>
              {cycleData.budgeted_hours ? secondsToHoursFormatNotSeconds(cycleData.budgeted_hours) : '- -'}
            </strong>
          </div>
          <div className='pc-stat-row'>
            <span>{intl.formatMessage({id: 'PROJECT.LABEL.HOURS_REALIZED'})}</span>
            <strong>{secondsToHoursFormatNotSeconds(cycleData.used_time)}</strong>
          </div>
        </div>
      </div>
    </section>
  )
}

export { PublicCycleView }





